// src/components/TiptapEditor.jsx
// Registers inline tools (AudioRecorder, PracticeTracker, Metronome, WhatDoYouHear, MemoryMatch)
// and exposes insert commands. Hides BubbleMenu for node selections and ensures a trailing paragraph.

import React, { useState, useRef, useEffect } from 'react';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import Typography from '@tiptap/extension-typography';
import SlashMenu from './SlashMenu';
import TextFormatMenu from './TextFormatMenu';
import AttendanceToggle from './AttendanceToggle';
import PDFDownloadButton from './PDFDownloadButton';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

// Inline/Block node views
import { AudioRecorderNode } from './tiptap/extensions/AudioRecorderNode.jsx';
import AttachmentNode from './tiptap/extensions/AttachmentNode.jsx';
import { PracticeTrackerNode } from './tiptap/extensions/PracticeTrackerNode.jsx';
import { MetronomeNode } from './tiptap/extensions/MetronomeNode.jsx';
import WhatDoYouHearNode from './tiptap/extensions/WhatDoYouHearNode.jsx';
import TimerToolNode from './tiptap/extensions/TimerToolNode.jsx';
import MemoryMatchNode from './tiptap/extensions/MemoryMatchNode.jsx';
import PracticeBingoNode from './tiptap/extensions/PracticeBingoNode.jsx';
import YouTubeLinkNode from './tiptap/extensions/YouTubeLinkNode.jsx';
import NoteReadingGameNode from './tiptap/extensions/NoteReadingGameNode.jsx';
import PerformanceInterruptionNode from './tiptap/extensions/PerformanceInterruptionNode.jsx'; 
import InteractiveVideoNode from './tiptap/extensions/InteractiveVideoNode.jsx';
import RandomisePracticeDiceNode from './tiptap/extensions/RandomisePracticeDiceNode.jsx';

const { FiCheckCircle, FiLoader, FiAlertCircle, FiEye } = FiIcons;

const TiptapEditor = ({
  content,
  onUpdate,
  title,
  savingStatus,
  readOnly = false,
  editorRef,
  onOpenToolPicker, // kept for compatibility (not required by new SlashMenu)
  lessonId,
  lessonsParticipantId,
  // IMPORTANT: pass the note's id and student id for inline tools
  lessonNoteId,
  studentId,
  masterStudentId,
  groupId,
  studentName,
  lessonDate,
  noteContentRef,
  placeholderText = 'Start typing your lesson notes…',
  // When true (group lessons), hide the single-student attendance toggle
  suppressIndividualAttendance = false,
  // NEW: required by tools that persist config
  schoolId,
  userId, // users.id (NOT auth uid)
  featureMap = null, // { [feature_code]: boolean }
  entitlementsLoading = false,
  // NEW: hide the "Read-only" badge when true
  suppressReadOnlyBanner = false,
  // NEW: render custom content directly under the title divider
  belowDividerContent = null,
}) => {
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashRange, setSlashRange] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const localEditorRef = useRef(null);
  const [showBubbleMenu] = useState(true);
  const lastAppliedContentRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2] },
        code: false,
        bulletList: { keepMarks: true, keepAttributes: true },
        orderedList: { keepMarks: true, keepAttributes: true },
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
        HTMLAttributes: { class: 'task-item-wrapper' },
      }),
      Placeholder.configure({
        placeholder: ({ editor, node, pos }) => {
          if (readOnly) return '';
          const sel = editor.state.selection;
          const from = sel.from, to = sel.to;
          const nodeStart = pos;
          const nodeEnd = pos + node.nodeSize;
          const isCaretInsideNode = from === to && from >= nodeStart && from <= nodeEnd;
          if (node.type.name !== 'paragraph') return '';
          const firstChild = editor.state.doc.firstChild;
          const isFirstParagraph = firstChild && firstChild.type.name === 'paragraph' && firstChild === node;
          if (isFirstParagraph) return (placeholderText || 'Start typing your lesson notes…');
          return isCaretInsideNode ? 'Type / to bring up menu options.' : '';
        },
        showOnlyWhenEditable: true,
        showOnlyCurrent: false,
        includeChildren: true,
        emptyNodeClass: 'is-empty',
      }),
      Underline,
      Typography,
      // Tools
      AudioRecorderNode,
      AttachmentNode,
      PracticeTrackerNode,
      MetronomeNode,
      WhatDoYouHearNode,
      MemoryMatchNode,
      PracticeBingoNode,
      YouTubeLinkNode,
      NoteReadingGameNode,
      PerformanceInterruptionNode,
      InteractiveVideoNode,
      TimerToolNode,
      RandomisePracticeDiceNode,
    ],
    content: content || { type: 'doc', content: [{ type: 'paragraph' }] },
    onUpdate: ({ editor }) => {
      if (!readOnly && typeof onUpdate === 'function') {
        onUpdate(editor.getJSON());
      }
    },
    onCreate: ({ editor }) => {
      if (readOnly) return;

      // Stash IDs & context in storage (mutate; do NOT reassign editor.storage)
      const mms = editor.storage.mms || (editor.storage.mms = {});
      mms.lessonNoteId    = lessonNoteId    || null;
      mms.studentId       = studentId       || null;
      mms.masterStudentId = masterStudentId || null;
      mms.groupId         = groupId         || null;
      mms.schoolId        = schoolId        || null;
      mms.userId          = userId          || null;
      // Entitlements for gating (safe to store even if readOnly; but onCreate bails on readOnly anyway)
      mms.featureMap = (featureMap && typeof featureMap === 'object') ? featureMap : {};
      mms.entitlementsLoading = !!entitlementsLoading;

      // Track "/" to open slash menu near caret
      editor.on('selectionUpdate', ({ editor }) => {
        const selection = editor.state.selection;
        const $from = selection.$from;
        const text = ($from.nodeBefore && $from.nodeBefore.text) || '';
        if (text.endsWith('/')) {
          const range = { from: $from.pos - 1, to: $from.pos };
          const editorView = editor.view;
          const end = editorView.state.selection.$to;
          const coords = editorView.coordsAtPos(end.pos);
          setMenuPosition({ top: coords.top, left: coords.left, bottom: coords.bottom });
          setSlashRange(range);
          setShowSlashMenu(true);
        } else {
          setShowSlashMenu(false);
          setSlashRange(null);
          setMenuPosition(null);
        }
      });
    },
    editable: !readOnly,
  });

  // Keep storage in sync if props change (mutate only)
  useEffect(() => {
    if (!editor) return;
    const mms = editor.storage.mms || (editor.storage.mms = {});
    mms.lessonNoteId    = lessonNoteId    || null;
    mms.studentId       = studentId       || null;
    mms.masterStudentId = masterStudentId || null;
    mms.groupId         = groupId         || null;
    mms.schoolId        = schoolId        || null;
    mms.userId          = userId          || null;
    // Keep entitlements in sync (mutate only)
    mms.featureMap = (featureMap && typeof featureMap === 'object') ? featureMap : {};
    mms.entitlementsLoading = !!entitlementsLoading;
  }, [editor, lessonNoteId, studentId, masterStudentId, groupId, schoolId, userId, featureMap, entitlementsLoading]);

/// sync editor content on note change (fast + safe)
  useEffect(() => {
    if (!editor) return;

    const incoming = content || { type: 'doc', content: [{ type: 'paragraph' }] };
    const incomingStr = JSON.stringify(incoming);

    // Avoid re-applying identical content (stringify once)
    if (lastAppliedContentRef.current === incomingStr) return;
    lastAppliedContentRef.current = incomingStr;

    // emitUpdate = false prevents save loops
    editor.commands.setContent(incoming, false);
  }, [editor, content]);

  // Expose the editor instance
  useEffect(() => {
    if (editorRef && editor) editorRef.current = editor;
  }, [editor, editorRef]);

  // Focus editor on mount (non-readonly)
  useEffect(() => {
    if (!editor || readOnly) return;
    editor.commands.focus('end');
    // run once
    }, []);

  // Editor theme CSS hooks
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .ProseMirror p.is-empty::before,
      .prose .ProseMirror p.is-empty::before {
        content: attr(data-placeholder);
        color: #9CA3AF;
        opacity: 0.9;
        pointer-events: none;
        white-space: pre-wrap;
      }
      .ProseMirror ul[data-type="taskList"] { list-style: none; padding-left: 0; }
      .ProseMirror ul[data-type="taskList"] li { display: flex; align-items: center; margin: 0.5rem 0; }
      .ProseMirror ul[data-type="taskList"] li > label { flex: 0 0 auto; margin-right: 0.5rem; display: flex; align-items: center; justify-content: center; height: 1.25em; }
      .ProseMirror ul[data-type="taskList"] li > div { flex: 1 1 auto; display: flex; align-items: center; min-height: 1.5em; }
      .ProseMirror ul[data-type="taskList"] li[data-checked="true"] > div { text-decoration: line-through; color: #9CA3AF; }
      .ProseMirror p,
      .ProseMirror h1,
      .ProseMirror h2,
      .ProseMirror h3,
      .ProseMirror ul,
      .ProseMirror ol,
      .ProseMirror blockquote,
      .ProseMirror pre,
      .ProseMirror hr { position: relative; padding-left: 1rem; }
      .ProseMirror p::after,
      .ProseMirror h1::after,
      .ProseMirror h2::after,
      .ProseMirror h3::after,
      .ProseMirror ul::after,
      .ProseMirror ol::after,
      .ProseMirror blockquote::after,
      .ProseMirror pre::after,
      .ProseMirror hr::after { content: '⋮⋮'; position: absolute; left: -0.5rem; top: 50%; transform: translateY(-50%); color: #9CA3AF; cursor: grab; opacity: 0; transition: opacity 0.2s; width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; border-radius: 4px; font-size: 12px; line-height: 1; }
      .ProseMirror p.is-empty::after { content: none; }
      .ProseMirror p:hover::after,
      .ProseMirror h1:hover::after,
      .ProseMirror h2:hover::after,
      .ProseMirror h3:hover::after,
      .ProseMirror ul:hover::after,
      .ProseMirror ol:hover::after,
      .ProseMirror blockquote:hover::after,
      .ProseMirror pre:hover::after,
      .ProseMirror hr:hover::after { opacity: 0.8; }
      .ProseMirror p::after:hover,
      .ProseMirror h1::after:hover,
      .ProseMirror h2::after:hover,
      .ProseMirror h3::after:hover,
      .ProseMirror ul::after:hover,
      .ProseMirror ol::after:hover,
      .ProseMirror blockquote::after:hover,
      .ProseMirror pre::after:hover,
      .ProseMirror hr::after:hover { opacity: 1 !important; background-color: #F3F4F6; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleCloseSlashMenu = () => {
    setShowSlashMenu(false);
    setSlashRange(null);
    setMenuPosition(null);
  };

  const handleOpenToolPicker = () => {
    if (typeof onOpenToolPicker === 'function') onOpenToolPicker();
    handleCloseSlashMenu();
  };

  // Bubble menu should not show on node selections (inline tools)
  const bubbleShouldShow = ({ editor, from, to }) => {
    if (!showBubbleMenu) return false;
    if (from === to && editor.isActive('audioRecorder')) return false;
    if (from === to && editor.isActive('attachment')) return false; // hide bubble on attachment block
    if (from === to && editor.isActive('practiceTracker')) return false;
    if (from === to && editor.isActive('metronome')) return false;
    if (from === to && editor.isActive('whatDoYouHear')) return false;
    if (from === to && editor.isActive('memoryMatch')) return false;
    if (from === to && editor.isActive('practiceBingo')) return false;
    if (from === to && editor.isActive('interactiveVideo')) return false;
    if (from === to && editor.isActive('performanceInterruption')) return false;
    if (from === to && editor.isActive('timerTool')) return false;
    if (from === to && editor.isActive('randomisePracticeDice')) return false;
    const sel = editor.state.selection;
    if (sel && sel.node) return false;
    return !editor.isActive('code') && from !== to;
  };

  return (
    <div className="relative">
      <div className="mb-8 flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{title}</h1>
        <div className="flex items-center gap-4">
          {/* Hide single-student attendance when this is a group lesson */}
          {lessonId && !readOnly && !suppressIndividualAttendance && lessonsParticipantId && (
            <AttendanceToggle
              lessonId={lessonId}
              lessonsParticipantId={lessonsParticipantId}
              disabled={readOnly || !lessonsParticipantId}
              prefetch={{ schoolId }}
            />
          )}
          <PDFDownloadButton
            contentRef={noteContentRef}
            studentName={studentName}
            lessonDate={lessonDate}
            disabled={readOnly}
          />
          {readOnly && !suppressReadOnlyBanner && (
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <SafeIcon icon={FiEye} className="text-gray-400" />
              <span>Read-only</span>
            </div>
          )}
        </div>
      </div>

      <div className="h-px bg-gray-200 mb-8"></div>
      {belowDividerContent}

      <div
        ref={localEditorRef}
        className="relative bg-white rounded-md"
        style={{ cursor: readOnly ? 'default' : 'text', minHeight: '60vh', padding: '1rem' }}
      >
        {editor && !readOnly && (
          <BubbleMenu
            editor={editor}
            shouldShow={bubbleShouldShow}
            tippyOptions={{ duration: 150, placement: 'top' }}
            className="bg-white rounded-md shadow-md border border-gray-200 overflow-hidden"
          >
            <TextFormatMenu editor={editor} />
          </BubbleMenu>
        )}

        <EditorContent
          editor={editor}
          className={`prose prose-lg max-w-none focus:outline-none ${readOnly ? 'cursor-default' : ''}`}
          spellCheck
        />

        {showSlashMenu && slashRange && menuPosition && !readOnly && (
          <SlashMenu
            editor={editor}
            range={slashRange}
            onClose={handleCloseSlashMenu}
            menuPosition={menuPosition}
            onOpenToolPicker={handleOpenToolPicker}
          />
        )}
      </div>

      {renderSavingStatus({ savingStatus })}
    </div>
  );
};

// Extracted to keep JSX above tidy
function renderSavingStatus({ savingStatus }) {
  if (!savingStatus) return null;
  let icon, text, bgColor, tone;
  switch (savingStatus) {
    case 'saving': icon = FiLoader; text = 'Saving...'; bgColor = 'bg-blue-100'; tone = 'text-blue-500'; break;
    case 'saved':  icon = FiCheckCircle; text = 'Saved'; bgColor = 'bg-green-100'; tone = 'text-green-500'; break;
    case 'error':  icon = FiAlertCircle; text = 'Error saving'; bgColor = 'bg-red-100'; tone = 'text-red-500'; break;
    default: return null;
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className={`fixed bottom-4 right-4 ${bgColor} text-sm px-3 py-2 rounded-md flex items-center gap-2 shadow-md`}
    >
      {savingStatus === 'saving' ? (
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
          <SafeIcon icon={icon} className={tone} />
        </motion.div>
      ) : (
        <SafeIcon icon={icon} className={tone} />
      )}
      <span>{text}</span>
    </motion.div>
  );
}

export default TiptapEditor;